import { DrawInteraction, MeasureInteraction } from '../interactions/Interaction';
import { MIDataExchange } from '../exchange/DataExchange';
import { MISpatialAnalysis } from '../core/SpatialAnalysis';
import { MISpatialAnalysis3D } from '../analysis/SpatialAnalysis3D';
import { GeoJsonSource } from '../sources/Source';
import { VectorLayer } from '../layers/Layer';
import { GeoJsonDataSource3D } from '../layers/Layer3D';

/** Pixel offset from the view container. `Offset` is retained for legacy callers. */
export type GISToolBarOffset = { x: number; y: number } | [number, number];
export interface GISToolBarOptions {
  offset?: GISToolBarOffset;
  Offset?: GISToolBarOffset;
  draggable?: boolean;
}

export type GISView = {
  viewport?: HTMLElement;
  getContainer?: () => HTMLElement;
  addInteraction?: (interaction: any) => void;
  removeInteraction?: (interaction: any) => void;
  getLayers?: () => any[];
  addLayer?: (layer: any) => void;
  exportImage?: (options?: any) => Promise<Blob>;
  [method: string]: any;
};

type ViewKind = 'map' | 'scene';
type ToolId = 'draw' | 'measure' | 'navigate' | 'layers' | 'exchange' | 'analysis' | 'terrain' | 'lighting' | 'api';
type CatalogItem = { name: string; category: string; args: string; description: string; views: ViewKind[] };

const PANEL_TITLES: Record<ToolId, string> = {
  draw: 'Drawing', measure: 'Measurement', navigate: 'Navigation', layers: 'Layers',
  exchange: 'Import & export', analysis: 'Analysis', terrain: 'Terrain & environment',
  lighting: 'Lighting', api: 'SDK method catalog',
};

/*
 * This is deliberately an explicit allow-list rather than prototype reflection:
 * lifecycle methods, event plumbing and engine escape hatches must never become
 * executable merely because they happen to be functions on a view.
 */
const CATALOG: CatalogItem[] = [
  ['getCenter', 'Navigation', '[]', 'Read map center', ['map']], ['setCenter', 'Navigation', '[[77.209, 28.614], false]', 'Set map center', ['map']],
  ['getZoom', 'Navigation', '[]', 'Read zoom level', ['map']], ['setZoom', 'Navigation', '[12, false]', 'Set zoom level', ['map']],
  ['getRotation', 'Navigation', '[]', 'Read bearing in radians', ['map']], ['setRotation', 'Navigation', '[0, false]', 'Set bearing in radians', ['map']],
  ['getPitch', 'Navigation', '[]', 'Read pitch', ['map']], ['setPitch', 'Navigation', '[0]', 'Set pitch', ['map']],
  ['flyTo', 'Navigation', '[{"center":[77.2,28.6],"zoom":10}]', 'Fly camera', ['map']], ['easeTo', 'Navigation', '[{"center":[77.2,28.6],"zoom":10}]', 'Ease camera', ['map']],
  ['panBy', 'Navigation', '[[20, 20]]', 'Pan by pixels', ['map']], ['jumpTo', 'Navigation', '[{"zoom":10}]', 'Jump camera', ['map']],
  ['zoomIn', 'Navigation', '[]', 'Zoom in', ['map']], ['zoomOut', 'Navigation', '[]', 'Zoom out', ['map']], ['resetNorth', 'Navigation', '[]', 'Reset north', ['map']],
  ['snapToNorth', 'Navigation', '[]', 'Snap north', ['map']], ['stop', 'Navigation', '[]', 'Stop camera animation', ['map']],
  ['getExtent', 'Navigation', '[]', 'Read visible extent', ['map']], ['fitExtent', 'Navigation', '[{"minX":77,"minY":28,"maxX":78,"maxY":29}]', 'Fit an extent', ['map']],
  ['getScale', 'Navigation', '[]', 'Read display scale', ['map']], ['getResolution', 'Navigation', '[]', 'Read ground resolution', ['map']],
  ['toMapCoordinate', 'Navigation', '[[100, 100]]', 'Convert pixel to longitude/latitude', ['map']], ['toPixelCoordinate', 'Navigation', '[[77.2, 28.6]]', 'Convert longitude/latitude to pixel', ['map']],
  ['setMinZoom', 'Navigation', '[0]', 'Set minimum zoom', ['map']], ['setMaxZoom', 'Navigation', '[22]', 'Set maximum zoom', ['map']],
  ['setMinPitch', 'Navigation', '[0]', 'Set minimum pitch', ['map']], ['setMaxPitch', 'Navigation', '[60]', 'Set maximum pitch', ['map']],
  ['setMaxBounds', 'Navigation', '[null]', 'Clear or constrain map bounds', ['map']], ['setPadding', 'Navigation', '[{"top":0,"right":0,"bottom":0,"left":0}]', 'Set camera padding', ['map']],
  ['getPadding', 'Navigation', '[]', 'Read camera padding', ['map']], ['cameraForBounds', 'Navigation', '[{"minX":77,"minY":28,"maxX":78,"maxY":29}]', 'Calculate camera for bounds', ['map']],
  ['getProjection', 'Projection', '[]', 'Read active map projection', ['map']], ['isMoving', 'State', '[]', 'Read camera movement state', ['map']],
  ['isZooming', 'State', '[]', 'Read zoom animation state', ['map']], ['isRotating', 'State', '[]', 'Read rotation animation state', ['map']],
  ['fitScreenCoordinates', 'Navigation', '[[10,10],[300,300],0]', 'Fit two screen points', ['map']],
  ['getFreeCameraOptions', 'Navigation', '[]', 'Read free-camera options', ['map']],
  ['setFreeCameraOptions', 'Navigation', '[{}]', 'Apply a partial free-camera update to the current camera', ['map']],
  ['setStyle', 'Style', '[{"version":8,"sources":{},"layers":[]}]', 'Replace style JSON', ['map']], ['getStyle', 'Style', '[]', 'Read style JSON', ['map']],
  ['setProjection', 'Style', '["mercator"]', 'Set map projection', ['map']], ['setSky', 'Style', '[{}]', 'Set sky style', ['map']],
  ['setFog', 'Style', '[{}]', 'Set fog style', ['map']], ['setLight', 'Style', '[{}]', 'Set light style', ['map']],
  ['setTerrain', 'Terrain', '[null]', 'Set/clear raster DEM terrain', ['map']], ['getTerrain', 'Terrain', '[]', 'Read terrain specification', ['map']],
  ['setLocale', 'Style', '[{}]', 'Set localization strings', ['map']], ['setRenderWorldCopies', 'Style', '[true]', 'Toggle world copies', ['map']],
  ['addSource', 'Sources', '["source-id", {"type":"geojson","data":{"type":"FeatureCollection","features":[]}}]', 'Add raw source', ['map']],
  ['removeSource', 'Sources', '["source-id"]', 'Remove raw source', ['map']], ['getSource', 'Sources', '["source-id"]', 'Get raw source', ['map']],
  ['isSourceLoaded', 'Sources', '["source-id"]', 'Test source loading', ['map']], ['areTilesLoaded', 'Sources', '[]', 'Test tile loading', ['map']],
  ['addImage', 'Style', '["image-id", {}]', 'Add a sprite image', ['map']], ['removeImage', 'Style', '["image-id"]', 'Remove a sprite image', ['map']],
  ['hasImage', 'Style', '["image-id"]', 'Test sprite image', ['map']], ['listImages', 'Style', '[]', 'List sprite images', ['map']],
  ['loadImage', 'Style', '["https://example.com/image.png"]', 'Load image URL', ['map']], ['updateImage', 'Style', '["image-id", {}]', 'Update sprite image', ['map']],
  ['addSprite', 'Style', '["sprite-id","https://example.com/sprite"]', 'Add sprite', ['map']], ['removeSprite', 'Style', '["sprite-id"]', 'Remove sprite', ['map']],
  ['setPaintProperty', 'Layers', '["layer-id","fill-color","#0088ff"]', 'Set native paint property', ['map']],
  ['setLayoutProperty', 'Layers', '["layer-id","visibility","visible"]', 'Set native layout property', ['map']],
  ['setFilter', 'Layers', '["layer-id",["==","kind","road"]]', 'Set native layer filter', ['map']],
  ['setLayerZoomRange', 'Layers', '["layer-id",0,22]', 'Set native layer zoom range', ['map']], ['moveLayer', 'Layers', '["layer-id"]', 'Move native layer', ['map']],
  ['getLayer', 'Layers', '["layer-id"]', 'Get native layer', ['map']], ['getPaintProperty', 'Layers', '["layer-id","fill-color"]', 'Get native paint property', ['map']],
  ['getLayoutProperty', 'Layers', '["layer-id","visibility"]', 'Get native layout property', ['map']], ['getFilter', 'Layers', '["layer-id"]', 'Get native filter', ['map']],
  ['queryRenderedFeatures', 'Query', '[]', 'Query rendered features', ['map']], ['querySourceFeatures', 'Query', '["source-id"]', 'Query source features', ['map']],
  ['setFeatureState', 'Query', '[{"source":"source-id","id":1},{"selected":true}]', 'Set feature state', ['map']],
  ['getFeatureState', 'Query', '[{"source":"source-id","id":1}]', 'Get feature state', ['map']],
  ['removeFeatureState', 'Query', '[{"source":"source-id","id":1}]', 'Remove feature state', ['map']],
  ['showTileBoundaries', 'Diagnostics', '[true]', 'Show tile boundaries', ['map']], ['showCollisionBoxes', 'Diagnostics', '[true]', 'Show collision boxes', ['map']],
  ['showPadding', 'Diagnostics', '[true]', 'Show camera padding', ['map']], ['showOverdrawInspector', 'Diagnostics', '[true]', 'Show overdraw inspector', ['map']],
  ['repaint', 'Diagnostics', '[true]', 'Force repaint mode', ['map']], ['loaded', 'Diagnostics', '[]', 'Test map loaded', ['map']],
  ['getLayers', 'Layers', '[]', 'List SDK-managed layers', ['map']], ['getLayerById', 'Layers', '["layer-id"]', 'Find SDK layer by id', ['map']],
  ['getLayersByType', 'Layers', '["vector"]', 'Find SDK layers by type', ['map']], ['clearLayers', 'Layers', '[]', 'Remove all SDK layers', ['map']],
  ['isStyleLoaded', 'Diagnostics', '[]', 'Test style loaded', ['map']], ['updateSize', 'Diagnostics', '[]', 'Resize map after container change', ['map']],
  ['render', 'Diagnostics', '[]', 'Request map repaint', ['map']], ['exportImage', 'Export', '[]', 'Export map canvas image', ['map']],
  ['setCamera', 'Navigation', '[{"position":[77.2,28.6,10000],"heading":0,"tilt":-45}, false]', 'Set scene camera', ['scene']],
  ['getCamera', 'Navigation', '[]', 'Read scene camera', ['scene']], ['flyTo', 'Navigation', '[{"position":[77.2,28.6,10000],"heading":0,"tilt":-45}]', 'Fly scene camera', ['scene']],
  ['flyToTarget', 'Navigation', '[[77.2,28.6,10000],{"duration":1000,"heading":0,"tilt":-45}]', 'Fly to a geographic target adapter', ['scene']],
  ['zoomToTarget', 'Navigation', '[[77.2,28.6,10000],{"heading":0,"tilt":-45}]', 'Immediately zoom to a geographic target adapter', ['scene']],
  ['lookAt', 'Navigation', '[[77.2,28.6,0]]', 'Look at location', ['scene']], ['orbitAround', 'Navigation', '[[77.2,28.6,0]]', 'Orbit target (returns stop handle)', ['scene']],
  ['setNavigationMode', 'Navigation', '["orbit"]', 'Set orbit/walk/fly/first-person', ['scene']], ['getNavigationMode', 'Navigation', '[]', 'Read navigation mode', ['scene']],
  ['screenToGeo', 'Query', '[[100,100]]', 'Convert screen pixel to geographic point', ['scene']], ['geoToScreen', 'Query', '[[77.2,28.6,0]]', 'Convert geographic point to pixel', ['scene']],
  ['pick', 'Query', '[[100,100]]', 'Pick object at pixel', ['scene']], ['drillPick', 'Query', '[[100,100]]', 'Pick all objects at pixel', ['scene']],
  ['pickPosition', 'Query', '[[100,100]]', 'Pick geographic position', ['scene']], ['pickEllipsoid', 'Query', '[[100,100]]', 'Pick ellipsoid position', ['scene']],
  ['getPickRay', 'Query', '[[100,100]]', 'Get camera pick ray', ['scene']], ['pickFromRay', 'Query', '[[77.2,28.6,1000],[0,0,-1]]', 'Pick from ray', ['scene']],
  ['getGlobeHeight', 'Terrain', '[77.2,28.6]', 'Get terrain height', ['scene']], ['sampleHeight', 'Terrain', '[[77.2,28.6]]', 'Sample scene height', ['scene']],
  ['clampToHeight', 'Terrain', '[[77.2,28.6]]', 'Clamp point to scene', ['scene']], ['sampleHeightMostDetailed', 'Terrain', '[[77.2,28.6]]', 'Detailed terrain samples', ['scene']],
  ['clampToHeightMostDetailed', 'Terrain', '[[77.2,28.6]]', 'Detailed clamps', ['scene']], ['setTerrain', 'Terrain', '[{"source":"https://example.com/terrain"}]', 'Set terrain', ['scene']],
  ['setGlobeShow', 'Terrain', '[true]', 'Show globe', ['scene']], ['setGlobeBaseColor', 'Terrain', '["#1b4965"]', 'Set globe base color', ['scene']],
  ['setAtmosphere', 'Environment', '[true]', 'Set atmosphere', ['scene']], ['setEnvironment', 'Environment', '[{"sun":true,"moon":true}]', 'Set sky, sun and moon', ['scene']],
  ['setUnderground', 'Environment', '[{"enabled":true,"transparency":0.5}]', 'Configure underground mode', ['scene']],
  ['setPostProcessing', 'Environment', '[{"fxaa":true}]', 'Set post processing', ['scene']], ['getPostProcessing', 'Environment', '[]', 'Read post processing', ['scene']],
  ['setLightingTime', 'Lighting', '["2025-01-01T12:00:00.000Z"]', 'Set lighting time (ISO string accepted by panel)', ['scene']],
  ['getLightingTime', 'Lighting', '[]', 'Read lighting time', ['scene']], ['enableSunTracking', 'Lighting', '[1]', 'Enable real-time sun tracking', ['scene']],
  ['disableSunTracking', 'Lighting', '[]', 'Disable sun tracking', ['scene']], ['getSunPosition', 'Lighting', '[]', 'Read sun azimuth/elevation', ['scene']],
  ['addLight', 'Lighting', '[{"type":"directional","intensity":1}]', 'Add light', ['scene']], ['removeLight', 'Lighting', '["light-id"]', 'Remove light', ['scene']],
  ['getLights', 'Lighting', '[]', 'List lights', ['scene']], ['setShadowOptions', 'Lighting', '[{"enabled":true}]', 'Set shadows', ['scene']],
  ['getShadowOptions', 'Lighting', '[]', 'Read shadow options', ['scene']], ['setSceneMode', 'Navigation', '["3d",0]', 'Set 2D, Columbus or 3D mode', ['scene']],
  ['morphTo2D', 'Navigation', '[0]', 'Morph to 2D', ['scene']], ['morphTo3D', 'Navigation', '[0]', 'Morph to 3D', ['scene']],
  ['morphToColumbusView', 'Navigation', '[0]', 'Morph to Columbus View', ['scene']], ['getSceneMode', 'Navigation', '[]', 'Read scene mode', ['scene']],
  ['setWidgetVisible', 'Widgets', '["homeButton",true]', 'Toggle a Viewer widget', ['scene']], ['setBaseLayerPickerVisible', 'Widgets', '[true]', 'Toggle base layer picker', ['scene']],
  ['setGeocoderVisible', 'Widgets', '[true]', 'Toggle geocoder', ['scene']], ['setFullscreenButtonVisible', 'Widgets', '[true]', 'Toggle fullscreen', ['scene']],
  ['setVrButtonVisible', 'Widgets', '[true]', 'Toggle VR', ['scene']], ['setSceneModePickerVisible', 'Widgets', '[true]', 'Toggle scene picker', ['scene']],
  ['setNavigationHelpButtonVisible', 'Widgets', '[true]', 'Toggle navigation help', ['scene']], ['setProjectionPickerVisible', 'Widgets', '[true]', 'Toggle projection picker', ['scene']],
  ['setInfoBoxVisible', 'Widgets', '[true]', 'Toggle info box', ['scene']], ['setSelectionIndicatorVisible', 'Widgets', '[true]', 'Toggle selection indicator', ['scene']],
  ['setHomeButtonVisible', 'Widgets', '[true]', 'Toggle home', ['scene']], ['setAnimationVisible', 'Widgets', '[true]', 'Toggle animation', ['scene']],
  ['setTimelineVisible', 'Widgets', '[true]', 'Toggle timeline', ['scene']], ['moveForward', 'Navigation', '[100]', 'Move camera forward', ['scene']],
  ['moveBackward', 'Navigation', '[100]', 'Move camera backward', ['scene']], ['moveLeft', 'Navigation', '[100]', 'Move camera left', ['scene']],
  ['moveRight', 'Navigation', '[100]', 'Move camera right', ['scene']], ['moveUp', 'Navigation', '[100]', 'Move camera up', ['scene']],
  ['moveDown', 'Navigation', '[100]', 'Move camera down', ['scene']], ['rotateLeft', 'Navigation', '[10]', 'Rotate camera left', ['scene']],
  ['rotateRight', 'Navigation', '[10]', 'Rotate camera right', ['scene']], ['rotateUp', 'Navigation', '[10]', 'Rotate camera up', ['scene']],
  ['rotateDown', 'Navigation', '[10]', 'Rotate camera down', ['scene']], ['twistLeft', 'Navigation', '[10]', 'Twist camera left', ['scene']],
  ['twistRight', 'Navigation', '[10]', 'Twist camera right', ['scene']], ['setRequestRenderMode', 'Diagnostics', '[true]', 'Toggle request render mode', ['scene']],
  ['setMSAA', 'Diagnostics', '[4]', 'Set MSAA samples', ['scene']], ['setFXAA', 'Diagnostics', '[true]', 'Toggle FXAA', ['scene']],
  ['showFramesPerSecond', 'Diagnostics', '[true]', 'Show frame rate', ['scene']],
  ['getLayers', 'Layers', '[]', 'List SDK-managed layers', ['scene']], ['clearLayers', 'Layers', '[]', 'Remove all SDK layers', ['scene']],
  ['getElevation', 'Terrain', '[[77.2,28.6]]', 'Get terrain elevation', ['scene']], ['computeViewRectangle', 'Query', '[]', 'Read visible geographic rectangle', ['scene']],
  ['setDepthTestAgainstTerrain', 'Terrain', '[true]', 'Toggle terrain depth testing', ['scene']], ['setGroundAtmosphere', 'Environment', '[true]', 'Toggle ground atmosphere', ['scene']],
  ['setDynamicAtmosphereLighting', 'Environment', '[true]', 'Toggle dynamic atmosphere lighting', ['scene']],
  ['setPreloadAncestors', 'Terrain', '[true]', 'Toggle ancestor tile preload', ['scene']], ['setPreloadSiblings', 'Terrain', '[true]', 'Toggle sibling tile preload', ['scene']],
  ['setTileCacheSize', 'Terrain', '[100]', 'Set terrain tile cache size', ['scene']], ['addGlobeClippingPlane', 'Terrain', '[{"normal":[0,0,1],"distance":0}]', 'Add globe clipping plane', ['scene']],
  ['clearGlobeClippingPlanes', 'Terrain', '[]', 'Clear globe clipping planes', ['scene']], ['setViewWithOrientation', 'Navigation', '[[77.2,28.6,10000],{"heading":0,"pitch":-45,"roll":0}]', 'Set camera view/orientation', ['scene']],
  ['lookAtTransform', 'Navigation', '[null]', 'Reset camera transform', ['scene']], ['updateSize', 'Diagnostics', '[]', 'Resize scene after container change', ['scene']],
  ['render', 'Diagnostics', '[]', 'Request scene render', ['scene']], ['toJSON', 'Export', '[]', 'Serialize scene state', ['scene']],
].map(([name, category, args, description, views]) => ({ name: name as string, category: category as string, args: args as string, description: description as string, views: views as ViewKind[] }));

export const GIS_TOOLBAR_ANALYSIS_CATALOG: ReadonlyArray<{ name: string; args: string; description: string }> = [
  { name: 'buffer', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}}, 1]', description: '2D Turf buffer (kilometres by default)' },
  { name: 'centroid', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}}]', description: '2D centroid' },
  { name: 'area', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Polygon","coordinates":[[[77,28],[78,28],[78,29],[77,28]]]}}]', description: '2D geodesic area' },
  { name: 'getLength', args: '[{"type":"Feature","properties":{},"geometry":{"type":"LineString","coordinates":[[77,28],[78,29]]}}]', description: '2D line length' },
  { name: 'bbox', args: '[{"type":"FeatureCollection","features":[]}]', description: '2D bounding box' },
  { name: 'union', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}},{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.3,28.7]}}]', description: '2D feature union' },
  { name: 'intersection', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Polygon","coordinates":[[[77,28],[78,28],[78,29],[77,28]]]}},{"type":"Feature","properties":{},"geometry":{"type":"Polygon","coordinates":[[[77.5,28.5],[78.5,28.5],[78.5,29.5],[77.5,28.5]]]}}]', description: '2D feature intersection' },
  { name: 'difference', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Polygon","coordinates":[[[77,28],[78,28],[78,29],[77,28]]]}},{"type":"Feature","properties":{},"geometry":{"type":"Polygon","coordinates":[[[77.5,28.5],[78.5,28.5],[78.5,29.5],[77.5,28.5]]]}}]', description: '2D feature difference' },
  { name: 'convexHull', args: '[{"type":"FeatureCollection","features":[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}},{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.3,28.7]}}]}]', description: '2D convex hull' },
  { name: 'distance', args: '[[77.2,28.6],[77.3,28.7]]', description: '2D point distance' },
  { name: 'voronoi', args: '[{"type":"FeatureCollection","features":[]}]', description: '2D Voronoi polygons' },
  { name: 'nearestPoint', args: '[[77.2,28.6],{"type":"FeatureCollection","features":[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.3,28.7]}}]}]', description: 'Find nearest 2D point' },
  { name: 'pointsWithinPolygon', args: '[{"type":"FeatureCollection","features":[]},{"type":"Feature","properties":{},"geometry":{"type":"Polygon","coordinates":[[[77,28],[78,28],[78,29],[77,28]]]}}]', description: 'Filter points within polygon' },
  { name: 'booleanContains', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}},{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}}]', description: 'Test geometry containment' },
  { name: 'booleanIntersects', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}},{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}}]', description: 'Test geometry intersection' },
  { name: 'booleanWithin', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}},{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}}]', description: 'Test geometry within' },
  { name: 'simplify', args: '[{"type":"Feature","properties":{},"geometry":{"type":"LineString","coordinates":[[77,28],[77.1,28.1],[77.2,28.2]]}}]', description: 'Simplify feature' },
  { name: 'along', args: '[{"type":"Feature","properties":{},"geometry":{"type":"LineString","coordinates":[[77,28],[78,29]]}},1]', description: 'Point along a line' },
  { name: 'bboxPolygon', args: '[[77,28,78,29]]', description: 'Create polygon from bounding box' },
  { name: 'midpoint', args: '[[77.2,28.6],[77.3,28.7]]', description: '2D midpoint' },
  { name: 'bearing', args: '[[77.2,28.6],[77.3,28.7]]', description: '2D bearing' },
  { name: 'destination', args: '[[77.2,28.6],1,45]', description: 'Destination point' },
  { name: 'dissolve', args: '[{"type":"FeatureCollection","features":[]}]', description: 'Dissolve feature collection' },
  { name: 'transformRotate', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}},45]', description: 'Rotate feature' },
  { name: 'transformScale', args: '[{"type":"Feature","properties":{},"geometry":{"type":"Point","coordinates":[77.2,28.6]}},2]', description: 'Scale feature' },
  { name: 'viewshed', args: '[{"observer":[77.2,28.6,10]}]', description: '3D viewshed' },
  { name: 'lineOfSight', args: '[{"observer":[77.2,28.6,10],"targets":[[77.21,28.61,10]]}]', description: '3D line of sight' },
  { name: 'measure', args: '[{"type":"distance"}]', description: 'Interactive 3D measurement' },
  { name: 'shadowAnalysis', args: '[{"type":"instant"}]', description: '3D shadow analysis' },
  { name: 'crossSection', args: '[{"line":[[77.2,28.6,0],[77.21,28.61,0]],"surfaces":["terrain"]}]', description: '3D cross section' },
  { name: 'floodAnalysis', args: '[{"waterLevel":10}]', description: '3D flood analysis' },
  { name: 'volumeComparison', args: '[{"preModificationDEM":"before.tif","postModificationDEM":"after.tif"}]', description: '3D cut/fill volume comparison' },
  { name: 'terrainProfile', args: '[{"path":[[77.2,28.6],[77.21,28.61]]}]', description: '3D terrain profile' },
  { name: 'bestPath', args: '[{"start":[77.2,28.6],"end":[77.21,28.61],"constraints":{}}]', description: '3D best path' },
  { name: 'mobilityAnalysis', args: '[{"extent":{"west":77.1,"south":28.5,"east":77.3,"north":28.7},"vehicleProfile":"wheeled-light"}]', description: '3D mobility analysis' },
  { name: 'trajectory', args: '[{"origin":[77.2,28.6,10],"launchAngle":45,"azimuth":45,"speed":100}]', description: '3D trajectory' },
  { name: 'ballisticCalculation', args: '[{"observerLocation":[77.2,28.6],"targetLocation":[77.21,28.61],"gunSpeed":500}]', description: 'Ballistic calculation' },
] as const;

type IconName = 'draw' | 'measure' | 'navigate' | 'layers' | 'exchange' | 'analysis' | 'terrain' | 'lighting' | 'api' | 'point' | 'line' | 'polygon' | 'circle' | 'rectangle' | 'freehand' | 'distance' | 'area' | 'bearing' | 'extent' | 'camera' | 'orbit' | 'walk' | 'globe' | 'minimize' | 'maximize' | 'close' | 'run' | 'undo' | 'stop' | 'remove' | 'import' | 'export' | 'image' | 'visible' | 'hidden' | 'action';

/** Creates a small inline SVG. Keeping the paths here makes the toolbar portable
 * in plain SDK installs: it has no icon font, image asset, or React dependency. */
function icon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '16'); svg.setAttribute('height', '16');
  svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round'); svg.setAttribute('aria-hidden', 'true');
  const paths: Record<IconName, string> = {
    draw: '<path d="M4 20 16 8l2 2L6 22H4z"/><path d="m14 6 2-2 4 4-2 2"/>',
    measure: '<path d="m4 16 12-12 4 4L8 20H4z"/><path d="m9 11 2 2m1-5 2 2m1-5 2 2"/>',
    navigate: '<circle cx="12" cy="12" r="8"/><path d="m15 9-2 6-6 2 2-6z"/>',
    layers: '<path d="m12 3 9 5-9 5-9-5z"/><path d="m3 12 9 5 9-5M3 16l9 5 9-5"/>',
    exchange: '<path d="M7 7h11l-3-3m3 3-3 3M17 17H6l3 3m-3-3 3-3"/>',
    analysis: '<path d="M4 19 9 5l6 14M6.5 14h5M15 7h5m-2.5-2.5v5"/>',
    terrain: '<path d="m3 20 7-12 4 6 2-3 5 9z"/>',
    lighting: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2m-2.9-7.1-1.4 1.4M6.3 17.7l-1.4 1.4m0-14.2 1.4 1.4m11.4 11.4 1.4 1.4"/>',
    api: '<path d="M8 4H5a2 2 0 0 0-2 2v3m18 0V6a2 2 0 0 0-2-2h-3M8 20H5a2 2 0 0 1-2-2v-3m18 0v3a2 2 0 0 1-2 2h-3"/><path d="M9 9h6v6H9z"/>',
    point: '<circle cx="12" cy="12" r="3"/><path d="M12 3v4m0 10v4M3 12h4m10 0h4"/>',
    line: '<circle cx="5" cy="17" r="2"/><circle cx="19" cy="7" r="2"/><path d="m7 16 10-8"/>',
    polygon: '<path d="m5 18 3-12 10-2 2 12-9 4z"/><circle cx="5" cy="18" r="1"/><circle cx="8" cy="6" r="1"/><circle cx="18" cy="4" r="1"/><circle cx="20" cy="16" r="1"/>',
    circle: '<circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="1"/>',
    rectangle: '<rect x="4" y="6" width="16" height="12" rx="1"/><path d="M8 6v2m8-2v2M8 16v2m8-2v2"/>',
    freehand: '<path d="M3 16c3-9 5 5 8-5s5 5 10-6"/><path d="m17 4 4 1-2 3"/>',
    distance: '<path d="m4 17 13-13 3 3L7 20H4z"/><path d="m9 12 2 2m1-5 2 2m1-5 2 2"/>',
    area: '<path d="m5 18 3-12 10-2 2 12-9 4z"/><path d="M9 9h6m-7 4h8"/>',
    bearing: '<circle cx="12" cy="12" r="8"/><path d="m15.5 8.5-2 5-5 2 2-5z"/><path d="M12 2v2"/>',
    extent: '<path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5"/><path d="M7 7h10v10H7z"/>',
    camera: '<rect x="3" y="7" width="14" height="10" rx="2"/><path d="m17 10 4-2v8l-4-2z"/><circle cx="10" cy="12" r="2"/>',
    orbit: '<circle cx="12" cy="12" r="3"/><path d="M4 12a8 8 0 0 1 14-5m2-1v5h-5"/>',
    walk: '<circle cx="12" cy="5" r="2"/><path d="m10 21 1-7-3-3 2-3 2 3 3 1m-2-4 2-2"/>',
    globe: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/>',
    minimize: '<path d="M5 12h14"/>', maximize: '<path d="M12 5v14m-7-7h14"/>', close: '<path d="m6 6 12 12M18 6 6 18"/>',
    run: '<path d="m8 5 11 7-11 7z"/>', undo: '<path d="M9 7 4 12l5 5"/><path d="M4 12h10a5 5 0 1 1 0 10"/>',
    stop: '<rect x="6" y="6" width="12" height="12" rx="1"/>', remove: '<path d="M4 7h16M10 11v5m4-5v5M9 7l1-3h4l1 3m-9 0 1 13h10l1-13"/>',
    import: '<path d="M12 3v12m-4-4 4 4 4-4M5 21h14"/>', export: '<path d="M12 15V3m-4 4 4-4 4 4M5 21h14"/>',
    image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9" r="1.5"/><path d="m21 16-5-5L5 20"/>',
    visible: '<path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z"/><circle cx="12" cy="12" r="2.5"/>',
    hidden: '<path d="m3 3 18 18M10.6 6.2A10 10 0 0 1 12 6c6.5 0 10 6 10 6a18 18 0 0 1-3.2 3.8M6.2 6.2C3.6 8 2 12 2 12s3.5 6 10 6a10 10 0 0 0 3.4-.6"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/>',
    action: '<circle cx="12" cy="12" r="8"/><path d="M12 8v8m-4-4h8"/>',
  };
  svg.innerHTML = paths[name];
  return svg;
}

function makeIconButton(name: string, glyph: IconName = 'action'): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button'; b.title = name; b.setAttribute('aria-label', name);
  b.appendChild(icon(glyph));
  Object.assign(b.style, { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '30px', minHeight: '30px', border: '0', borderRadius: '5px', padding: '6px', background: 'transparent', color: '#eaf2ff', cursor: 'pointer' });
  b.onmouseenter = () => { if (!b.disabled) b.style.background = '#28527a'; };
  b.onmouseleave = () => { if (!b.getAttribute('aria-pressed') || b.getAttribute('aria-pressed') === 'false') b.style.background = 'transparent'; };
  return b;
}

function stringify(value: any): string {
  try { return JSON.stringify(value, (_key, v) => v instanceof Date ? v.toISOString() : typeof v === 'function' ? '[Function]' : v, 2); }
  catch { return String(value); }
}

/** Standalone, framework-free GIS toolbar attached to a single MIMap or MISceneView. */
export class GISToolBar {
  readonly element: HTMLDivElement;
  private readonly view: GISView;
  private readonly kind: ViewKind;
  private readonly host: HTMLElement;
  private readonly buttons = new Map<ToolId, HTMLButtonElement>();
  private panel: HTMLDivElement | null = null;
  private active: ToolId | null = null;
  private interaction: any = null;
  private dragCleanup: (() => void) | null = null;
  private panelDragCleanup: (() => void) | null = null;
  private panelPointerCleanup: (() => void) | null = null;
  private activePointerCleanup: (() => void) | null = null;
  private layoutCleanup: (() => void) | null = null;
  private disposables: Array<{ remove?: () => void; stop?: () => void; clear?: () => void }> = [];
  private resultGeneration = 0;
  private previousHostPosition = '';
  private didSetHostPosition = false;
  private minimized = false;
  private destroyed = false;

  constructor(view: GISView, options: GISToolBarOptions = {}) {
    this.view = view;
    this.kind = typeof view.addInteraction === 'function' ? 'map' : 'scene';
    const host = view.viewport || view.getContainer?.() || (view as any)._container;
    if (!host || !(host instanceof HTMLElement)) throw new Error('GISToolBar requires an MIMap or MISceneView with a DOM container.');
    this.host = host;
    if (getComputedStyle(host).position === 'static') {
      this.previousHostPosition = host.style.position;
      host.style.position = 'relative';
      this.didSetHostPosition = true;
    }
    this.element = document.createElement('div');
    this.element.className = 'migis-gis-toolbar';
    this.element.setAttribute('role', 'toolbar');
    this.element.setAttribute('aria-label', 'MIGIS GIS tools');
    const [x, y] = this.normaliseOffset(options.offset ?? options.Offset);
    Object.assign(this.element.style, {
      position: 'absolute', left: `${x}px`, top: `${y}px`, zIndex: '20', minWidth: '92px', maxWidth: 'calc(100% - 12px)',
      background: '#102a43', border: '1px solid #3d5a80', borderRadius: '7px', boxShadow: '0 4px 16px #001b2e88',
      color: '#eaf2ff', userSelect: 'none', touchAction: 'none',
    });
    this.build(options.draggable !== false);
    host.appendChild(this.element);
    this.clamp();
    this.watchLayout();
  }

  update(options: GISToolBarOptions = {}): void {
    if (this.destroyed) return;
    const offset = options.offset ?? options.Offset;
    if (offset) { const [x, y] = this.normaliseOffset(offset); this.element.style.left = `${x}px`; this.element.style.top = `${y}px`; this.clamp(); }
    if (options.draggable !== undefined) this.setDraggable(options.draggable);
    this.element.style.display = '';
  }

  private normaliseOffset(offset?: GISToolBarOffset): [number, number] {
    const value: any = offset || [8, 8];
    const x = Array.isArray(value) ? value[0] : value.x;
    const y = Array.isArray(value) ? value[1] : value.y;
    return [Number.isFinite(x) ? x : 8, Number.isFinite(y) ? y : 8];
  }

  private build(draggable: boolean): void {
    const header = document.createElement('div');
    Object.assign(header.style, { display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 4px 2px 8px', borderBottom: '1px solid #3d5a80', cursor: draggable ? 'move' : 'default' });
    const title = document.createElement('strong'); title.textContent = 'GIS'; title.style.font = '600 12px system-ui,sans-serif'; title.style.flex = '1';
    const min = makeIconButton('Minimize toolbar', 'minimize'); min.style.padding = '3px 7px'; min.onclick = () => this.toggleMinimize();
    header.append(title, min); this.element.appendChild(header);
    this.setDraggable(draggable, header);
    const tools: Array<[ToolId, string, IconName]> = [
      ['draw', 'Draw geometry', 'draw'], ['measure', 'Measure geometry', 'measure'],
      ['navigate', 'Navigate view', 'navigate'], ['layers', 'Manage layers', 'layers'],
      ['exchange', 'Import and export data', 'exchange'], ['analysis', 'Run spatial analysis', 'analysis'],
      ['terrain', 'Terrain and environment', 'terrain'], ['lighting', 'Lighting and shadows', 'lighting'],
      ['api', 'Search SDK method catalog', 'api'],
    ];
    const stack = document.createElement('div'); stack.className = 'migis-gis-toolbar-tools';
    Object.assign(stack.style, { display: 'grid', gridTemplateColumns: '1fr', padding: '3px' });
    tools.forEach(([id, name, glyph]) => {
      const b = makeIconButton(name, glyph); b.dataset.tool = id; b.setAttribute('aria-pressed', 'false');
      b.onclick = () => this.openPanel(id); this.buttons.set(id, b); stack.appendChild(b);
    });
    this.element.appendChild(stack);
  }

  private toggleMinimize(): void {
    this.minimized = !this.minimized;
    const stack = this.element.querySelector('.migis-gis-toolbar-tools') as HTMLElement | null;
    if (stack) stack.style.display = this.minimized ? 'none' : '';
    if (this.minimized) this.closePanel();
    const min = this.element.querySelector('[aria-label="Minimize toolbar"]') as HTMLButtonElement | null;
    if (min) {
      const name = this.minimized ? 'Maximize toolbar' : 'Minimize toolbar';
      min.title = name; min.setAttribute('aria-label', name); min.replaceChildren(icon(this.minimized ? 'maximize' : 'minimize'));
    }
  }

  private setDraggable(enabled: boolean, handle?: HTMLElement): void {
    this.dragCleanup?.(); this.dragCleanup = null;
    if (!enabled) this.activePointerCleanup?.();
    const target = handle || (this.element.firstElementChild as HTMLElement | null);
    if (!enabled || !target) { if (target) target.style.cursor = 'default'; return; }
    target.style.cursor = 'move';
    const down = (event: PointerEvent) => {
      if ((event.target as HTMLElement).closest('button')) return;
      event.preventDefault();
      const rect = this.element.getBoundingClientRect(); const host = this.host.getBoundingClientRect();
      const dx = event.clientX - rect.left; const dy = event.clientY - rect.top;
      const move = (e: PointerEvent) => { this.element.style.left = `${e.clientX - host.left - dx}px`; this.element.style.top = `${e.clientY - host.top - dy}px`; this.clamp(); };
      const up = () => { this.activePointerCleanup?.(); };
      this.activePointerCleanup?.();
      this.activePointerCleanup = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        this.activePointerCleanup = null;
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up, { once: true });
      window.addEventListener('pointercancel', up, { once: true });
    };
    target.addEventListener('pointerdown', down);
    this.dragCleanup = () => target.removeEventListener('pointerdown', down);
  }

  private clamp(): void {
    const left = parseFloat(this.element.style.left) || 0, top = parseFloat(this.element.style.top) || 0;
    const maxX = Math.max(0, this.host.clientWidth - this.element.offsetWidth), maxY = Math.max(0, this.host.clientHeight - this.element.offsetHeight);
    this.element.style.left = `${Math.min(maxX, Math.max(0, left))}px`; this.element.style.top = `${Math.min(maxY, Math.max(0, top))}px`;
    if (this.panel) this.positionPanel(this.panel);
  }

  /** A split pane can resize without a window resize, so observe the host too. */
  private watchLayout(): void {
    const relayout = () => { if (!this.destroyed) this.clamp(); };
    // Keep a window listener even with ResizeObserver: viewport changes can
    // precede a flex-host ResizeObserver delivery by a frame.
    window.addEventListener('resize', relayout);
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(relayout);
      observer.observe(this.host);
      this.layoutCleanup = () => {
        observer.disconnect();
        window.removeEventListener('resize', relayout);
      };
      return;
    }
    this.layoutCleanup = () => window.removeEventListener('resize', relayout);
  }

  private openPanel(id: ToolId): void {
    if (this.active === id) { this.closePanel(); return; }
    this.closePanel(); this.active = id;
    const b = this.buttons.get(id); if (b) { b.setAttribute('aria-pressed', 'true'); b.style.background = '#28527a'; }
    const panel = document.createElement('div'); panel.className = 'migis-gis-toolbar-panel'; panel.setAttribute('role', 'dialog'); panel.setAttribute('aria-label', PANEL_TITLES[id]);
    Object.assign(panel.style, { position: 'absolute', left: '0', top: '0', boxSizing: 'border-box', overflow: 'auto', padding: '9px', background: '#f8fbff', color: '#102a43', border: '1px solid #3d5a80', borderRadius: '7px', boxShadow: '0 4px 16px #001b2e55', font: '12px/1.35 system-ui,sans-serif', userSelect: 'text' });
    const heading = document.createElement('div'); Object.assign(heading.style, { display: 'flex', alignItems: 'center', marginBottom: '7px' });
    const h = document.createElement('strong'); h.textContent = PANEL_TITLES[id]; h.style.flex = '1';
    const close = makeIconButton(`Close ${PANEL_TITLES[id]}`, 'close'); Object.assign(close.style, { color: '#102a43', padding: '0 6px' }); close.onclick = () => this.closePanel();
    heading.append(h, close); panel.appendChild(heading); this.element.appendChild(panel); this.panel = panel;
    this.setPanelDraggable(panel, heading);
    ({ draw: () => this.drawPanel(panel), measure: () => this.measurePanel(panel), navigate: () => this.navigatePanel(panel), layers: () => this.layersPanel(panel), exchange: () => this.exchangePanel(panel), analysis: () => this.analysisPanel(panel), terrain: () => this.terrainPanel(panel), lighting: () => this.lightingPanel(panel), api: () => this.apiPanel(panel) })[id]();
    requestAnimationFrame(() => { this.positionPanel(panel); });
  }

  private positionPanel(panel: HTMLElement): void {
    // Calculate in host coordinates, rather than against the window, so a
    // narrow split pane cannot make this absolute child overflow its map.
    const inset = 6;
    const maxWidth = Math.max(0, this.host.clientWidth - inset * 2);
    const maxHeight = Math.max(0, this.host.clientHeight - inset * 2);
    panel.style.width = `${Math.min(350, maxWidth)}px`;
    panel.style.maxHeight = `${Math.min(570, maxHeight)}px`;
    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;
    const toolbarLeft = this.element.offsetLeft;
    const toolbarTop = this.element.offsetTop;
    const rightX = toolbarLeft + this.element.offsetWidth + inset;
    const leftX = toolbarLeft - panelWidth - inset;
    const hostWidth = this.host.clientWidth;
    const hostHeight = this.host.clientHeight;
    const desiredX = rightX + panelWidth <= hostWidth - inset ? rightX : leftX;
    const x = Math.min(Math.max(inset, desiredX), Math.max(inset, hostWidth - panelWidth - inset));
    const y = Math.min(Math.max(inset, toolbarTop), Math.max(inset, hostHeight - panelHeight - inset));
    panel.style.right = 'auto';
    panel.style.left = `${x - toolbarLeft}px`;
    panel.style.top = `${y - toolbarTop}px`;
  }

  /** Panels contain form/output content, so their heading doubles as a drag handle. */
  private setPanelDraggable(panel: HTMLElement, handle: HTMLElement): void {
    handle.style.cursor = 'move';
    const down = (event: PointerEvent) => {
      if ((event.target as HTMLElement).closest('button,input,textarea,select,label')) return;
      event.preventDefault();
      const panelRect = panel.getBoundingClientRect();
      const toolbarRect = this.element.getBoundingClientRect();
      const hostRect = this.host.getBoundingClientRect();
      const dx = event.clientX - panelRect.left;
      const dy = event.clientY - panelRect.top;
      const move = (e: PointerEvent) => {
        const left = e.clientX - toolbarRect.left - dx;
        const top = e.clientY - toolbarRect.top - dy;
        const minLeft = -this.element.offsetLeft;
        const maxLeft = this.host.clientWidth - this.element.offsetLeft - panel.offsetWidth;
        const minTop = -this.element.offsetTop;
        const maxTop = this.host.clientHeight - this.element.offsetTop - panel.offsetHeight;
        panel.style.right = 'auto';
        panel.style.left = `${Math.min(maxLeft, Math.max(minLeft, left))}px`;
        panel.style.top = `${Math.min(maxTop, Math.max(minTop, top))}px`;
      };
      const up = () => cleanup();
      const cleanup = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        if (this.panelPointerCleanup === cleanup) this.panelPointerCleanup = null;
      };
      // hostRect intentionally validates that a detached panel is never dragged.
      if (!hostRect.width || !hostRect.height) return;
      this.panelPointerCleanup?.();
      this.panelPointerCleanup = cleanup;
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up, { once: true });
      window.addEventListener('pointercancel', up, { once: true });
    };
    handle.addEventListener('pointerdown', down);
    this.panelDragCleanup = () => {
      handle.removeEventListener('pointerdown', down);
      this.panelPointerCleanup?.(); this.panelPointerCleanup = null;
    };
  }

  closePanel(): void {
    this.stopInteraction();
    this.panelDragCleanup?.(); this.panelDragCleanup = null;
    this.resultGeneration++;
    this.disposeResults();
    if (this.active) { const b = this.buttons.get(this.active); if (b) { b.setAttribute('aria-pressed', 'false'); b.style.background = 'transparent'; } }
    this.panel?.remove(); this.panel = null; this.active = null;
  }

  private message(panel: HTMLElement, text: string, error = false): void {
    let out = panel.querySelector('.migis-gis-output') as HTMLPreElement | null;
    if (!out) { out = document.createElement('pre'); out.className = 'migis-gis-output'; Object.assign(out.style, { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', background: '#e9f1f8', borderRadius: '4px', padding: '6px', margin: '8px 0 0', maxHeight: '180px', overflow: 'auto' }); panel.appendChild(out); }
    out.style.color = error ? '#a61b1b' : '#102a43'; out.textContent = text;
  }

  private form(panel: HTMLElement, label: string, value: string, run: (raw: string) => void): void {
    const l = document.createElement('label'); l.textContent = label; l.style.display = 'block'; l.style.fontWeight = '600';
    const input = document.createElement('textarea'); input.value = value; input.rows = 4; input.setAttribute('aria-label', label);
    Object.assign(input.style, { width: '100%', boxSizing: 'border-box', marginTop: '3px', font: '11px ui-monospace,monospace' });
    const go = makeIconButton(`Run ${label}`, 'run'); Object.assign(go.style, { marginTop: '5px', color: '#102a43', border: '1px solid #8da4ba' }); go.onclick = () => run(input.value);
    l.append(input, go); panel.appendChild(l);
  }

  private buttonRow(panel: HTMLElement, actions: Array<{ label: string; run: () => void; disabled?: boolean; icon?: IconName }>, name = 'Tool actions'): void {
    const row = document.createElement('div'); row.className = 'migis-gis-tool-group migis-gis-toolbar-subtoolbar'; row.setAttribute('role', 'toolbar'); row.setAttribute('aria-label', name);
    Object.assign(row.style, { display: 'flex', flexWrap: 'wrap', gap: '5px', margin: '5px 0' });
    actions.forEach(action => {
      const b = makeIconButton(action.label, action.icon || 'action');
      b.disabled = !!action.disabled;
      Object.assign(b.style, { color: '#102a43', border: '1px solid #8da4ba', cursor: action.disabled ? 'not-allowed' : 'pointer' });
      b.onclick = action.run; row.appendChild(b);
    });
    panel.appendChild(row);
  }

  private stopInteraction(): void {
    if (!this.interaction) return;
    try { this.view.removeInteraction?.(this.interaction); } catch {}
    try { this.interaction.destroy?.(); } catch {}
    this.interaction = null;
  }

  private trackResult(value: any, generation: number = this.resultGeneration): void {
    if (generation !== this.resultGeneration || this.destroyed) {
      try { value?.remove?.(); } catch {}
      try { value?.stop?.(); } catch {}
      try { value?.clear?.(); } catch {}
      return;
    }
    if (value && (typeof value.remove === 'function' || typeof value.stop === 'function' || typeof value.clear === 'function')) {
      this.disposables.push(value);
    }
  }

  private disposeResults(): void {
    const results = this.disposables.splice(0);
    results.forEach(result => {
      try { result.remove?.(); } catch {}
      try { result.stop?.(); } catch {}
      try { result.clear?.(); } catch {}
    });
  }

  private drawPanel(panel: HTMLElement): void {
    const unsupported = this.kind !== 'map';
    const intro = document.createElement('p'); intro.textContent = unsupported ? 'Drawing interactions are currently available on MIMap only.' : 'Select a geometry. Click to add vertices; double-click completes a line or polygon.'; panel.appendChild(intro);
    this.buttonRow(panel, ['point', 'line', 'polygon', 'circle', 'rectangle', 'freehand'].map(type => ({ label: `Draw ${type}`, icon: type as IconName, disabled: unsupported, run: () => {
      this.stopInteraction(); const draw = new DrawInteraction({ type: type as any }); this.interaction = draw; this.view.addInteraction!(draw);
      draw.on('drawend', (e: any) => this.message(panel, stringify(e.feature)));
      draw.on('drawabort', () => this.message(panel, 'Drawing aborted.'));
      this.message(panel, `Drawing ${type}.`);
    }})), 'Drawing tools');
    this.buttonRow(panel, [{ label: 'Undo drawing vertex', icon: 'undo', disabled: unsupported, run: () => this.interaction?.removeLastPoint?.() }, { label: 'Stop drawing', icon: 'stop', run: () => { this.stopInteraction(); this.message(panel, 'Drawing stopped.'); } }], 'Drawing tools');
  }

  private measurePanel(panel: HTMLElement): void {
    const unsupported = this.kind !== 'map';
    const p = document.createElement('p'); p.textContent = unsupported ? 'The 3D measurement API is available under Analysis → measure.' : 'Click to add points; double-click completes a measurement.'; panel.appendChild(p);
    this.buttonRow(panel, ['distance', 'area', 'bearing'].map(type => ({ label: `Measure ${type}`, icon: type as IconName, disabled: unsupported, run: () => {
      this.stopInteraction(); const measure = new MeasureInteraction({ type: type as any, units: 'metric' }); this.interaction = measure; this.view.addInteraction!(measure);
      measure.on('measure', (e: any) => this.message(panel, stringify(e)));
      measure.on('measureend', (e: any) => this.message(panel, stringify(e)));
    }})), 'Measurement tools');
    this.buttonRow(panel, [{ label: 'Stop measurement', icon: 'stop', run: () => { this.stopInteraction(); this.message(panel, 'Measurement stopped.'); } }], 'Measurement tools');
  }

  private navigatePanel(panel: HTMLElement): void {
    const actions = this.kind === 'map'
      ? [{ label: 'Zoom in', icon: 'maximize' as IconName, run: () => this.execute(panel, 'zoomIn', []) }, { label: 'Zoom out', icon: 'minimize' as IconName, run: () => this.execute(panel, 'zoomOut', []) }, { label: 'Reset north', icon: 'bearing' as IconName, run: () => this.execute(panel, 'resetNorth', []) }, { label: 'Read visible extent', icon: 'extent' as IconName, run: () => this.execute(panel, 'getExtent', []) }]
      : [{ label: 'Set orbit navigation', icon: 'orbit' as IconName, run: () => this.execute(panel, 'setNavigationMode', ['orbit']) }, { label: 'Set walk navigation', icon: 'walk' as IconName, run: () => this.execute(panel, 'setNavigationMode', ['walk']) }, { label: 'Set 3D scene mode', icon: 'globe' as IconName, run: () => this.execute(panel, 'setSceneMode', ['3d', 0]) }, { label: 'Read camera', icon: 'camera' as IconName, run: () => this.execute(panel, 'getCamera', []) }];
    this.buttonRow(panel, actions, 'Navigation tools');
    this.form(panel, 'Advanced navigation arguments (JSON array)', this.navigationArgs(), raw => {
      this.runJSON(panel, raw, args => this.execute(panel, this.kind === 'map' ? 'flyTo' : 'setCamera', args));
    });
  }

  private layersPanel(panel: HTMLElement): void {
    const layers = this.view.getLayers?.() || [];
    if (!layers.length) { const p = document.createElement('p'); p.textContent = 'No SDK-managed layers in this view.'; panel.appendChild(p); return; }
    layers.forEach((layer: any) => {
      const row = document.createElement('div'); row.className = 'migis-gis-tool-group migis-gis-toolbar-subtoolbar'; row.setAttribute('role', 'toolbar'); row.setAttribute('aria-label', `Layer controls for ${layer.id || layer.constructor?.name || 'layer'}`);
      Object.assign(row.style, { display: 'flex', gap: '6px', alignItems: 'center', margin: '4px 0' });
      const name = document.createElement('span'); name.textContent = layer.id || layer.constructor?.name || 'Layer'; name.style.flex = '1';
      let visible = layer.isVisible?.() ?? layer.visible !== false;
      const visibility = makeIconButton(`${visible ? 'Hide' : 'Show'} layer ${name.textContent}`, visible ? 'visible' : 'hidden');
      Object.assign(visibility.style, { color: '#102a43', border: '1px solid #8da4ba' });
      visibility.onclick = () => {
        try {
          visible = !visible; layer.setVisible?.(visible);
          const label = `${visible ? 'Hide' : 'Show'} layer ${name.textContent}`;
          visibility.title = label; visibility.setAttribute('aria-label', label); visibility.replaceChildren(icon(visible ? 'visible' : 'hidden'));
          this.message(panel, `${name.textContent} ${visible ? 'shown' : 'hidden'}.`);
        } catch (e) { this.message(panel, String(e), true); }
      };
      const remove = makeIconButton(`Remove layer ${name.textContent}`, 'remove'); Object.assign(remove.style, { color: '#102a43', border: '1px solid #8da4ba' }); remove.onclick = () => {
        try { this.view.removeLayer?.(layer); row.remove(); this.message(panel, `${name.textContent} removed.`); }
        catch (e) { this.message(panel, `Could not remove layer: ${e instanceof Error ? e.message : String(e)}`, true); }
      };
      row.append(visibility, name, remove); panel.appendChild(row);
    });
  }

  private exchangePanel(panel: HTMLElement): void {
    const file = document.createElement('input'); file.type = 'file'; file.setAttribute('aria-label', 'Import GIS data file'); file.accept = '.geojson,.json,.kml,.gpx,.csv,.wkt,.topojson,.gml,.citygml';
    const importButton = makeIconButton('Import selected GIS data file', 'import'); Object.assign(importButton.style, { color: '#102a43', border: '1px solid #8da4ba' }); importButton.onclick = async () => {
      const selected = file.files?.[0]; if (!selected) { this.message(panel, 'Choose a supported file first.', true); return; }
      try {
        const data = await MIDataExchange.import(selected, { format: 'auto' });
        const id = `toolbar-import-${Date.now()}`;
        const layer = this.kind === 'map'
          ? new VectorLayer({ id, source: new GeoJsonSource({ data }) })
          : new GeoJsonDataSource3D({ id, data });
        this.view.addLayer?.(layer);
        this.message(panel, stringify({ imported: selected.name, features: data?.features?.length ?? 0, layerId: id, data }));
      } catch (e) { this.message(panel, `Import failed: ${e instanceof Error ? e.message : String(e)}`, true); }
    };
    const importGroup = document.createElement('div'); importGroup.className = 'migis-gis-tool-group migis-gis-toolbar-subtoolbar'; importGroup.setAttribute('role', 'toolbar'); importGroup.setAttribute('aria-label', 'Import tools');
    Object.assign(importGroup.style, { display: 'flex', alignItems: 'center', gap: '5px', margin: '5px 0' });
    importGroup.append(file, importButton); panel.appendChild(importGroup);
    this.buttonRow(panel, [
      { label: 'Export current layers as GeoJSON', icon: 'export', run: () => this.exportData(panel, 'geojson') },
      { label: 'Export current layers as KML', icon: 'export', run: () => this.exportData(panel, 'kml') },
      { label: 'Export current layers as CSV', icon: 'export', run: () => this.exportData(panel, 'csv') },
      { label: 'Export current map image', icon: 'image', disabled: this.kind !== 'map', run: () => this.exportImage(panel) },
    ], 'Import and export tools');
  }

  private async exportData(panel: HTMLElement, format: any): Promise<void> {
    try {
      const layers = this.exportLayers();
      const blob = await MIDataExchange.export(layers, { format });
      this.download(blob, `migis-export.${format}`); this.message(panel, `Exported ${blob.size} bytes as ${format}.`);
    }
    catch (e) { this.message(panel, `Export failed: ${e instanceof Error ? e.message : String(e)}`, true); }
  }
  private async exportImage(panel: HTMLElement): Promise<void> {
    try { const blob = await this.view.exportImage?.({}); if (!blob) throw new Error('Image export is unavailable.'); this.download(blob, 'migis-map.png'); this.message(panel, `Exported image (${blob.size} bytes).`); }
    catch (e) { this.message(panel, `Image export failed: ${e instanceof Error ? e.message : String(e)}`, true); }
  }
  private download(blob: Blob, name: string): void { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 0); }

  private analysisPanel(panel: HTMLElement): void {
    const sceneAnalysis = ['viewshed', 'lineOfSight', 'measure', 'shadowAnalysis', 'crossSection', 'floodAnalysis', 'volumeComparison', 'terrainProfile', 'bestPath', 'mobilityAnalysis', 'trajectory', 'ballisticCalculation'];
    const supported = GIS_TOOLBAR_ANALYSIS_CATALOG.filter(a => this.kind === 'scene' || !sceneAnalysis.includes(a.name));
    const select = document.createElement('select'); select.setAttribute('aria-label', 'Spatial analysis method'); supported.forEach(a => { const o = document.createElement('option'); o.value = a.name; o.textContent = `${a.name} — ${a.description}`; select.appendChild(o); }); panel.appendChild(select);
    const args = document.createElement('textarea'); args.rows = 5; args.value = this.analysisArgs(supported[0]); args.setAttribute('aria-label', 'Analysis arguments JSON array'); args.style.width = '100%'; args.style.boxSizing = 'border-box';
    select.onchange = () => { const spec = supported.find(a => a.name === select.value); args.value = spec ? this.analysisArgs(spec) : '[]'; }; panel.appendChild(args);
    this.buttonRow(panel, [{
      label: 'Run selected spatial analysis', icon: 'run', run: () => this.runJSON(panel, args.value, values => {
        const spec = GIS_TOOLBAR_ANALYSIS_CATALOG.find(a => a.name === select.value)!;
        try {
          const result = !sceneAnalysis.includes(spec.name)
            ? (MISpatialAnalysis as any)[spec.name](...values)
            : (MISpatialAnalysis3D as any)[spec.name](...(spec.name === 'ballisticCalculation' ? values : [this.view, ...values]));
          const generation = this.resultGeneration;
          Promise.resolve(result).then(value => {
            this.trackResult(value, generation);
            if (generation === this.resultGeneration && !this.destroyed) this.message(panel, stringify(value));
          }).catch(e => {
            if (generation === this.resultGeneration && !this.destroyed) this.message(panel, `Analysis failed: ${e.message || e}`, true);
          });
        } catch (e) { this.message(panel, `Analysis failed: ${e instanceof Error ? e.message : String(e)}`, true); }
      }),
    }], 'Analysis tools');
  }

  private terrainPanel(panel: HTMLElement): void {
    if (this.kind === 'map') {
      this.buttonRow(panel, [{ label: 'Read terrain', icon: 'terrain', run: () => this.execute(panel, 'getTerrain', []) }, { label: 'Clear terrain', icon: 'remove', run: () => this.execute(panel, 'setTerrain', [null]) }], 'Terrain tools');
      this.form(panel, 'Map terrain JSON', '{"source":"dem-source","exaggeration":1}', raw => this.runJSONValue(panel, raw, value => this.execute(panel, 'setTerrain', [value])));
    } else {
      this.buttonRow(panel, [{ label: 'Show globe', icon: 'globe', run: () => this.execute(panel, 'setGlobeShow', [true]) }, { label: 'Hide globe', icon: 'hidden', run: () => this.execute(panel, 'setGlobeShow', [false]) }, { label: 'Read sun position', icon: 'lighting', run: () => this.execute(panel, 'getSunPosition', []) }], 'Terrain tools');
      this.form(panel, 'Scene terrain JSON', '{"source":"https://example.com/terrain","exaggeration":1}', raw => this.runJSONValue(panel, raw, value => this.execute(panel, 'setTerrain', [value])));
    }
  }
  private lightingPanel(panel: HTMLElement): void {
    if (this.kind !== 'scene') { panel.textContent = 'Lighting and shadow controls require MISceneView.'; return; }
    this.buttonRow(panel, [{ label: 'Enable sun tracking', icon: 'lighting', run: () => this.execute(panel, 'enableSunTracking', [1]) }, { label: 'Disable sun tracking', icon: 'hidden', run: () => this.execute(panel, 'disableSunTracking', []) }, { label: 'Read sun position', icon: 'action', run: () => this.execute(panel, 'getSunPosition', []) }], 'Lighting tools');
    this.form(panel, 'Shadows JSON', '{"enabled":true,"type":"soft","resolution":1024}', raw => this.runJSONValue(panel, raw, value => this.execute(panel, 'setShadowOptions', [value])));
  }
  private apiPanel(panel: HTMLElement): void {
    const search = document.createElement('input'); search.type = 'search'; search.placeholder = 'Search public SDK methods'; search.setAttribute('aria-label', 'Search GIS methods'); search.style.width = '100%'; search.style.boxSizing = 'border-box'; panel.appendChild(search);
    const list = document.createElement('div'); list.style.marginTop = '7px'; panel.appendChild(list);
    const render = () => {
      const q = search.value.trim().toLowerCase(); list.innerHTML = '';
      const analysisItems: CatalogItem[] = this.kind === 'scene'
        ? GIS_TOOLBAR_ANALYSIS_CATALOG.map(item => ({ ...item, category: '3D analysis', views: ['scene'] }))
        : [];
      const items = [...CATALOG, ...analysisItems].filter(item => item.views.includes(this.kind) && (!q || `${item.name} ${item.category} ${item.description}`.toLowerCase().includes(q)));
      const groups = new Map<string, CatalogItem[]>(); items.forEach(item => groups.set(item.category, [...(groups.get(item.category) || []), item]));
      Array.from(groups.entries()).forEach(([category, values]) => {
        const h = document.createElement('strong'); h.textContent = category; h.style.display = 'block'; h.style.marginTop = '8px'; list.appendChild(h);
        values.forEach(item => {
          const row = document.createElement('div'); row.className = 'migis-gis-tool-group migis-gis-toolbar-subtoolbar'; row.setAttribute('role', 'toolbar'); row.setAttribute('aria-label', `SDK method controls for ${item.name}`);
          Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 0' });
          const open = makeIconButton(`Configure ${item.name}: ${item.description}`, 'action'); Object.assign(open.style, { color: '#102a43', border: '1px solid #8da4ba', minWidth: '26px', minHeight: '26px', padding: '4px' });
          const description = document.createElement('span'); description.textContent = `${item.name} — ${item.description}`; description.title = `Arguments: ${item.args}`;
          open.onclick = () => this.methodForm(panel, item); row.append(open, description); list.appendChild(row);
        });
      });
      if (!items.length) list.textContent = 'No public, safe-to-run method matches this search.';
    };
    search.oninput = render; render();
  }

  private methodForm(panel: HTMLElement, item: CatalogItem): void {
    const old = panel.querySelector('.migis-gis-method-form'); old?.remove();
    const form = document.createElement('div'); form.className = 'migis-gis-method-form'; Object.assign(form.style, { marginTop: '8px', borderTop: '1px solid #ccd8e5', paddingTop: '7px' });
    const label = document.createElement('label'); label.textContent = `${item.name} arguments (valid JSON array)`; label.style.fontWeight = '600';
    const input = document.createElement('textarea'); input.rows = 4; input.value = this.catalogArgs(item); input.setAttribute('aria-label', `${item.name} arguments JSON array`); input.style.width = '100%'; input.style.boxSizing = 'border-box';
    const run = makeIconButton(`Run ${item.name}`, 'run'); Object.assign(run.style, { color: '#102a43', border: '1px solid #8da4ba' }); run.onclick = () => this.runJSON(panel, input.value, args => this.execute(panel, item.name, args));
    label.append(input); form.append(label, run); panel.appendChild(form);
  }

  private isVisible(layer: any): boolean {
    try { return layer?.isVisible?.() ?? layer?.visible !== false; }
    catch { return layer?.visible !== false; }
  }

  private sourceFor(layer: any): any {
    try { return layer?.getSource?.() ?? layer?.source ?? layer?._source; }
    catch { return layer?.source ?? layer?._source; }
  }

  /**
   * A style wrapper can render one logical source more than once. Prefer the
   * explicit source id supplied by wrappers; otherwise object identity is the
   * conservative (and lossless) fallback.
   */
  private sourceKey(layer: any, source: any): unknown {
    return layer?.exportSourceId ?? source?.exportSourceId ?? source?.sourceId ?? source?.id ?? source ?? layer;
  }

  private featuresFrom(value: any): any[] {
    if (!value) return [];
    if (typeof value === 'string') {
      try { return this.featuresFrom(JSON.parse(value)); }
      catch { return []; } // A URL is not local visible feature data.
    }
    if (Array.isArray(value)) return value.flatMap(item => this.featuresFrom(item));
    if (value.type === 'FeatureCollection' && Array.isArray(value.features)) return value.features.filter((feature: any) => feature?.geometry);
    if (value.type === 'Feature' && value.geometry) return [value];
    if (value.type && (value.coordinates || value.geometries)) return [{ type: 'Feature', properties: {}, geometry: value }];
    return [];
  }

  /** Prefer data the caller has already put in the visible view over examples. */
  private contextFeatures(): any[] {
    const layers = this.view.getLayers?.() || [];
    const seenSources = new Set<unknown>();
    const features: any[] = [];
    layers.filter((layer: any) => this.isVisible(layer)).forEach((layer: any) => {
      const source = this.sourceFor(layer);
      const key = this.sourceKey(layer, source);
      if (seenSources.has(key)) return;
      seenSources.add(key);
      const current: any[] = [];
      try {
        const layerFeatures = layer?.getFeatures?.();
        if (!layerFeatures?.then) current.push(...this.featuresFrom(layerFeatures));
      } catch {}
      try {
        const sourceFeatures = source?.getFeatures?.();
        if (!sourceFeatures?.then) current.push(...this.featuresFrom(sourceFeatures));
      } catch {}
      // Some raw/adopted wrappers expose only getSource().data (or data
      // directly), while SDK vector layers expose getFeatures(). Support both.
      current.push(...this.featuresFrom(source?.data ?? layer?.data));
      const seenFeatures = new Set<any>();
      current.forEach(feature => {
        // SDK sources retain Feature object identity; this prevents the normal
        // getFeatures()+data path from analysing each feature twice, without
        // collapsing intentionally duplicated feature records.
        if (!seenFeatures.has(feature)) { seenFeatures.add(feature); features.push(feature); }
      });
    });
    return features;
  }

  private exportLayers(): any[] {
    const seenSources = new Set<unknown>();
    return (this.view.getLayers?.() || []).filter((layer: any) => {
      if (!this.isVisible(layer)) return false;
      const key = this.sourceKey(layer, this.sourceFor(layer));
      if (seenSources.has(key)) return false;
      seenSources.add(key);
      return true;
    });
  }

  private currentCoordinate(): number[] | null {
    try {
      const center = this.view.getCenter?.();
      if (Array.isArray(center) && center.length >= 2 && center.every((value: any) => Number.isFinite(value))) return center.slice(0, 2);
      const position = this.view.getCamera?.()?.position;
      if (Array.isArray(position) && position.length >= 2 && position.every((value: any) => Number.isFinite(value))) return position.slice(0, 3);
    } catch {}
    return null;
  }

  private featureCoordinate(feature: any): number[] | null {
    let coordinates = feature?.geometry?.coordinates ?? feature?.coordinates;
    while (Array.isArray(coordinates) && Array.isArray(coordinates[0])) coordinates = coordinates[0];
    return Array.isArray(coordinates) && coordinates.length >= 2 && coordinates.every((value: any) => Number.isFinite(value))
      ? coordinates.slice(0, 3)
      : null;
  }

  private navigationArgs(): string {
    if (this.kind === 'map') {
      const center = this.currentCoordinate();
      const zoom = this.view.getZoom?.();
      return JSON.stringify([{ ...(center ? { center } : {}), ...(Number.isFinite(zoom) ? { zoom } : {}) }], null, 2);
    }
    try {
      const camera = this.view.getCamera?.();
      if (camera?.position) return JSON.stringify([camera, false], null, 2);
    } catch {}
    return '[]';
  }

  private analysisArgs(spec: { name: string; args: string }): string {
    const features = this.contextFeatures();
    const feature = features[0];
    const second = features[1];
    const collection = { type: 'FeatureCollection', features };
    if (this.kind === 'scene' && feature) {
      const points = features.map(featureItem => this.featureCoordinate(featureItem)).filter((point): point is number[] => !!point);
      if (spec.name === 'viewshed' && points[0]) return stringify([{ observer: points[0] }]);
      if (spec.name === 'lineOfSight' && points[0]) return stringify([{ observer: points[0], targets: points.slice(1) }]);
      if (spec.name === 'crossSection' && points.length >= 2) return stringify([{ line: points, surfaces: ['terrain'] }]);
      if (spec.name === 'terrainProfile' && points.length >= 2) return stringify([{ path: points }]);
      if (spec.name === 'bestPath' && points.length >= 2) return stringify([{ start: points[0], end: points[1], constraints: {} }]);
      if (spec.name === 'trajectory' && points[0]) return stringify([{ origin: points[0], launchAngle: 45, azimuth: 45, speed: 100 }]);
      if (spec.name === 'ballisticCalculation' && points.length >= 2) return stringify([{ observerLocation: points[0], targetLocation: points[1], gunSpeed: 500 }]);
    }
    if (feature) {
      const paired = new Set(['union', 'intersection', 'difference', 'booleanContains', 'booleanIntersects', 'booleanWithin']);
      if (paired.has(spec.name) && second) return stringify([feature, second]);
      if (['bbox', 'convexHull', 'voronoi', 'dissolve'].includes(spec.name)) return stringify([collection]);
      if (spec.name === 'nearestPoint' && second) return stringify([feature.geometry.coordinates, collection]);
      if (['distance', 'midpoint', 'bearing'].includes(spec.name) && second?.geometry?.type === 'Point' && feature.geometry?.type === 'Point') return stringify([feature.geometry.coordinates, second.geometry.coordinates]);
      if (spec.name === 'buffer') return stringify([feature, 1]);
      if (spec.name === 'along') return stringify([feature, 1]);
      if (['destination'].includes(spec.name) && feature.geometry?.type === 'Point') return stringify([feature.geometry.coordinates, 1, 45]);
      return stringify([feature]);
    }
    const coordinate = this.currentCoordinate();
    if (this.kind === 'scene' && coordinate) {
      if (spec.name === 'viewshed') return stringify([{ observer: coordinate }]);
      if (spec.name === 'lineOfSight') return stringify([{ observer: coordinate, targets: [] }]);
    }
    // An empty argument list is intentional. It produces a useful SDK error
    // instead of silently analysing arbitrary demonstration geometry.
    return '[]';
  }

  private catalogArgs(item: CatalogItem): string {
    const coordinate = this.currentCoordinate();
    const layer = (this.view.getLayers?.() || [])[0];
    if (this.kind === 'map' && ['flyTo', 'easeTo', 'jumpTo'].includes(item.name)) return this.navigationArgs();
    if (this.kind === 'map' && coordinate && item.name === 'setCenter') return stringify([coordinate, false]);
    if (this.kind === 'map' && coordinate && item.name === 'toPixelCoordinate') return stringify([coordinate]);
    if (this.kind === 'scene' && coordinate && ['setCamera', 'flyTo'].includes(item.name)) {
      try {
        const camera = this.view.getCamera?.();
        return stringify(item.name === 'setCamera' ? [camera, false] : [camera]);
      } catch {}
    }
    if (coordinate && ['flyToTarget', 'zoomToTarget', 'lookAt', 'orbitAround', 'geoToScreen', 'getGlobeHeight', 'sampleHeight', 'clampToHeight'].includes(item.name)) return stringify([coordinate]);
    if (layer?.id && ['getLayer', 'getLayerById', 'removeLayer', 'getFilter'].includes(item.name)) return stringify([layer.id]);
    // Catalog examples are documentation, not operational defaults. Do not
    // silently point an action at a made-up location when the current view has
    // no corresponding context.
    if (item.args.includes('77.') || item.args.includes('[[77')) return '[]';
    return item.args;
  }

  private runJSON(panel: HTMLElement, raw: string, next: (args: any[]) => void): void {
    try { const value = JSON.parse(raw); if (!Array.isArray(value)) throw new Error('Arguments must be a JSON array.'); next(value); }
    catch (e) { this.message(panel, `Invalid JSON arguments: ${e instanceof Error ? e.message : String(e)}`, true); }
  }
  private runJSONValue(panel: HTMLElement, raw: string, next: (value: any) => void): void {
    try { next(JSON.parse(raw)); }
    catch (e) { this.message(panel, `Invalid JSON value: ${e instanceof Error ? e.message : String(e)}`, true); }
  }
  private execute(panel: HTMLElement, name: string, args: any[]): void {
    const allowed = CATALOG.some(item => item.name === name && item.views.includes(this.kind));
    if (!allowed) { this.message(panel, `${name} is not supported by this view.`, true); return; }
    try {
      // JSON cannot construct MapLibre's opaque FreeCameraOptions object.
      // Apply a partial update to the real current object instead.
      if (name === 'setFreeCameraOptions' && this.kind === 'map') {
        const camera = this.view.getFreeCameraOptions?.();
        if (!camera) throw new Error('Free-camera options are unavailable in this MapLibre build.');
        Object.assign(camera, args[0] || {});
        this.view.setFreeCameraOptions(camera);
        this.message(panel, stringify({ ok: true, method: name }));
        return;
      }
      // Scene targets normally require Cesium Entity/DataSource instances,
      // which cannot safely be made from untrusted JSON. Geographic tuples are
      // a useful, real adapter to the existing public flyTo camera API.
      if ((name === 'flyToTarget' || name === 'zoomToTarget') && this.kind === 'scene') {
        const target = args[0];
        if (!Array.isArray(target) || target.length < 2) throw new Error('Target must be [longitude, latitude, height?].');
        const options = args[1] || {};
        const camera = {
          position: [target[0], target[1], target[2] ?? 10000],
          heading: Number.isFinite(options.heading) ? options.heading : 0,
          tilt: Number.isFinite(options.tilt) ? options.tilt : -45,
        };
        const result = name === 'flyToTarget'
          ? this.view.flyTo({ ...camera, duration: Number.isFinite(options.duration) ? options.duration : 1000 })
          : this.view.setCamera(camera, false);
        const generation = this.resultGeneration;
        Promise.resolve(result).then(value => {
          if (generation === this.resultGeneration && !this.destroyed) this.message(panel, stringify(value));
        }).catch(e => {
          if (generation === this.resultGeneration && !this.destroyed) this.message(panel, `${name} failed: ${e instanceof Error ? e.message : String(e)}`, true);
        });
        return;
      }
      const analysis = GIS_TOOLBAR_ANALYSIS_CATALOG.find(item => item.name === name);
      if (analysis && this.kind === 'scene') {
        const generation = this.resultGeneration;
        const result = name === 'ballisticCalculation'
          ? (MISpatialAnalysis3D as any)[name](...args)
          : (MISpatialAnalysis3D as any)[name](this.view, ...args);
        Promise.resolve(result).then(value => {
          this.trackResult(value, generation);
          if (generation === this.resultGeneration && !this.destroyed) this.message(panel, stringify(value));
        }).catch(e => {
          if (generation === this.resultGeneration && !this.destroyed) this.message(panel, `${name} failed: ${e instanceof Error ? e.message : String(e)}`, true);
        });
        return;
      }
      if (typeof this.view[name] !== 'function') { this.message(panel, `${name} is not supported by this view.`, true); return; }
      // Make Date input ergonomic while preserving strict JSON form safety.
      if (name === 'setLightingTime' && typeof args[0] === 'string') args = [new Date(args[0]), ...args.slice(1)];
      const result = this.view[name](...args);
      const generation = this.resultGeneration;
      Promise.resolve(result).then(value => {
        this.trackResult(value, generation);
        if (generation === this.resultGeneration && !this.destroyed) this.message(panel, stringify(value === undefined ? { ok: true, method: name } : value));
      }).catch(e => {
        if (generation === this.resultGeneration && !this.destroyed) this.message(panel, `${name} failed: ${e instanceof Error ? e.message : String(e)}`, true);
      });
    } catch (e) { this.message(panel, `${name} failed: ${e instanceof Error ? e.message : String(e)}`, true); }
  }

  destroy(): void {
    if (this.destroyed) return; this.destroyed = true;
    this.closePanel(); this.dragCleanup?.(); this.dragCleanup = null; this.activePointerCleanup?.(); this.layoutCleanup?.(); this.layoutCleanup = null; this.element.remove();
    if (this.didSetHostPosition) this.host.style.position = this.previousHostPosition;
  }
}

export const GIS_TOOLBAR_CATALOG: ReadonlyArray<CatalogItem> = CATALOG;